/**
 * Checks if current page is a static/special page that shouldn't be considered PLP
 * @param {string} path - Clean pathname without store code
 * @returns {boolean}
 */
export const isStaticPage = path => {
  const staticPages = [
    "about-us",
    "faq",
    "faq/ordering-and-returning",
    "faq/shipping",
    "faq/product-info",
    "faq/general",
    "careers",
    "klarna",
    "reviews",
    "help",
    "help/jiffy_first",
    "jiffytransfers",
    "sample-instructions",
    "custom",
    "made",
    "sameday",
    "shipping",
    "sweepstakes",
    "jiffy-copilot",
    "weaver-ai-studio",
    "cart",
    "checkout",
    "account",
    "contactus",
    "orders",
    "payments",
    "returns"
  ];

  return staticPages.includes(path.replace(/^\//, ""));
};

/**
 * Checks if current page is a brand page
 * @param {string} path - Clean pathname without store code
 * @param {URLSearchParams} [searchParams] - URL search parameters
 * @returns {boolean}
 */
export const isBrandPage = (path, searchParams) => {
  // All brand pages start with /brands/
  if (path.startsWith("/brands/")) {
    return true;
  }

  // Root with brand query param e.g. /?brand=Augusta+Sportswear
  return Boolean((path === "/" || path === "") && searchParams?.has("brand"));
};

/**
 * Checks if current page is a product listing page (PLP)
 * @param {string} path - Clean pathname without store code
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {boolean}
 */
export const isProductListingPage = (path, searchParams) => {
  // Main products index page with search/category params
  if (
    path === "/" &&
    (searchParams.has("search") ||
      searchParams.has("q") ||
      searchParams.has("category") ||
      searchParams.has("brand"))
  ) {
    return true;
  }

  // Category products pages
  // Check single-segment paths but exclude brands, static pages, and file extensions
  if (
    path.match(/^\/[^/]+$/) &&
    !path.match(/\.(html)$/) &&
    !isBrandPage(path, searchParams) &&
    !isStaticPage(path)
  ) {
    return true;
  }

  // Clothing products page
  if (path === "/clothing") {
    return true;
  }

  // Design/digital assets listing
  if (path === "/designs" || path.startsWith("/designs/")) {
    return true;
  }

  // Product collections/threads listing
  if (path === "/threads/products" || path === "/threads") {
    return true;
  }

  return false;
};

/**
 * Checks if current page is homepage
 * @param {string} path - Clean pathname without store code
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {boolean}
 */
export const isHomepage = (path, searchParams) =>
  (path === "/" || path === "") &&
  !searchParams.has("search") &&
  !searchParams.has("q") &&
  !searchParams.has("category") &&
  !searchParams.has("brand");

/**
 * Checks if current page is a product details page (PDP)
 * @param {string} path - Clean pathname without store code
 * @returns {boolean}
 */
export const isProductDetailsPage = path => {
  // Standard product pages ending with .html
  if (path.match(/^\/[^/]+\.html$/)) {
    return true;
  }

  // Custom printed product pages
  if (path.match(/^\/custom-printed-[^/]+\/[^/]+\.html$/)) {
    return true;
  }

  // Digital asset product pages
  if (path.match(/^\/design\/[^/]+$/)) {
    return true;
  }

  // Product collections/threads individual products
  if (path.match(/^\/threads\/products\/[^/]+$/)) {
    return true;
  }

  return false;
};

/**
 * Returns clean path without store code
 * @returns {string}
 */
export const getCleanPath = () => {
  const path = window.location.pathname;

  // Remove store code prefix if present (currently only 'ca' for Canada)
  const storeCodePattern = /^\/ca(?=\/|$)/;

  return path.replace(storeCodePattern, "") || "/";
};

/**
 * Detects the current page type based on URL patterns
 * @returns {Object} Object containing boolean flags for each page type
 */
export const detectPageType = () => {
  const cleanPath = getCleanPath();
  const searchParams = new URLSearchParams(window.location.search);

  return {
    isHomepage: isHomepage(cleanPath, searchParams),
    isProductListingPage: isProductListingPage(cleanPath, searchParams),
    isProductDetailsPage: isProductDetailsPage(cleanPath),
    isBrandPage: isBrandPage(cleanPath, searchParams)
  };
};

window.detectPageType = detectPageType;
